import { Area, UserAttributeDefs, ValidModelType } from "@iotv/datamodel-core";
import { PathDirection, Position, TimedPosition } from "@iotv/iotv-v3-types";
import { EFFLUENT_PATH_TYPE, EffluentPath } from "./EffluentPath";
import {
  allAreaData,
  AreaData,
  EffluentTracker,
  RunStats,
} from "./EffluentTracker";
import { LateralEffluentSpreader } from "./LateralEffluentSpreader";
import { PivotEffluentSpreader } from "./PivotEffluentSpreader";
import { SpreadingEventType } from "./SpreadingEvent";
import { DeadZoneType } from "./DeadZone";
import { TravellingEffluentSpreader } from "./TravellingEffluentSpreader";

const typeName = "SpreadingGun";
const dev = process.env.REACT_APP_DEBUG;
const debug = dev && false;

export type SpreadingGunProps = {
  throwDistance?: number;
  location?: Position;
  path?: TimedPosition[];
  spreading?: boolean;
  flow?: number;
};

export class SpreadingGun extends EffluentTracker {
  constructor(ob: any) {
    super(ob);
  }

  spreadingGunParent?: string;

  /**
   * Updated relevant runs. Allows runs of type SpreadingGun
   * @param runList
   * @returns runs of correct run type
   */
  getRelevantRuns(runList: EffluentPath[]): EffluentPath[] {
    debug && console.log("Using SpreadingGun specific - getRelevantRuns");
    let allowed = [
      EFFLUENT_PATH_TYPE.LateralEffluentSpreader,
      EFFLUENT_PATH_TYPE.PivotEffluentSpreader,
    ];
    let filteredRuns = runList.filter((run) => allowed.includes(run.pathType));
    return filteredRuns;
  }

  createSpreadingEventAndDeadZone(
    areas: Area[],
    runs: EffluentPath[],
  ): { Spreading: SpreadingEventType; Dead: DeadZoneType } {
    if (this.spreadingGunParent) {
      let eff: EffluentTracker = this;
      switch (this.spreadingGunParent) {
        case "LateralEffluentSpreader":
          eff = new LateralEffluentSpreader({ ...this });
          break;
        case "PivotEffluentSpreader":
          eff = new PivotEffluentSpreader({ ...this });

          break;
      }
      return eff.createSpreadingEventAndDeadZone(areas, runs);
    }
    return super.createSpreadingEventAndDeadZone(areas, runs);
  }

  getBestMatchedRun(
    runs: EffluentPath[],
    path: TimedPosition[] | undefined = this.path,
  ): RunStats | undefined {
    debug && console.log("Using default - getBestMatchedRun");

    // check there are runs to check against
    // check the path is of length
    if (runs.length === 0 || path === undefined || path.length === 0) {
      return undefined;
    }

    // sort runs by type
    const lateralRuns = runs.filter(
      (val) => val.pathType === EFFLUENT_PATH_TYPE.LateralEffluentSpreader,
    );
    const pivotsRuns = runs.filter(
      (val) => val.pathType === EFFLUENT_PATH_TYPE.PivotEffluentSpreader,
    );

    // construct a spreader for each
    const latSpreader = new LateralEffluentSpreader({ ...this });
    const pivSpreader = new PivotEffluentSpreader({ ...this });

    const bestLat = latSpreader.getBestMatchedRun(lateralRuns, path);
    const bestPiv = pivSpreader.getBestMatchedRun(pivotsRuns, path);

    const options = [bestLat, bestPiv]
      .filter((val) => val !== undefined)
      .sort((a, b) => a.distance - b.distance);

    if (options.length === 0) {
      return undefined;
    } else {
      return options[0];
    }
  }

  /**
   * Manually Updates
   * - filteredPath (getExpectedRun -> filterOutPathOutliers)
   * - runDifference (getExpectedRun)
   * - mappedRun (this)
   * - mappedPath (this)
   * - last irrigated (this)
   * @param runs
   * @param lastTimestamp
   */
  updateMappedRun(runs: EffluentPath[], lastTimestamp: number) {
    // create a lateral & effluent version.
    let bestMatched = this.getBestMatchedRun(runs);
    if (bestMatched) {
      let eff: EffluentTracker;
      switch (bestMatched.run.pathType) {
        case EFFLUENT_PATH_TYPE.LateralEffluentSpreader:
          eff = new LateralEffluentSpreader(this);

          break;
        case EFFLUENT_PATH_TYPE.PivotEffluentSpreader:
          eff = new PivotEffluentSpreader(this);

          break;
        default:
          eff = new TravellingEffluentSpreader(this);

          break;
      }
      eff.updateMappedRun(runs, lastTimestamp);

      this.filteredPath = eff.filteredPath;
      this.runDifference = eff.runDifference;
      this.mappedRun = eff.mappedRun;
      this.mappedPath = eff.mappedPath;
      this.lastIrrigated = lastTimestamp;
      this.spreadingGunParent = eff.type;
    } else {
      super.updateMappedRun(runs, lastTimestamp);
    }
  }

  /**
   * Function to get the spread LatLngs, and area (m^2), of all 3 conditions
   */
  getSpreadOfAllRuns(relevantRun?: EffluentPath): allAreaData {
    let effluentSpreader: EffluentTracker = this;
    if (relevantRun) {
      switch (relevantRun.pathType) {
        case EFFLUENT_PATH_TYPE.LateralEffluentSpreader:
          effluentSpreader = new LateralEffluentSpreader(this);
          break;

        case EFFLUENT_PATH_TYPE.PivotEffluentSpreader:
          effluentSpreader = new PivotEffluentSpreader(this);

          break;
      }
      return effluentSpreader.getSpreadOfAllRuns(relevantRun);
    }

    // spread for raw
    let raw = this.getSpreadOfRawRun(relevantRun);
    // spread for filtered
    let filtered = this.getSpreadOfFilteredRun(relevantRun);
    // spread for mapped
    let mapped = relevantRun
      ? this.getSpreadOfMappedRun(relevantRun)
      : undefined;

    return { raw, filtered, mapped };
  }

  /**
   * Function to get the spread LatLngs, and area (m^2), of the raw run
   * @returns LatLngs and area (m^2) obj
   */
  getSpreadOfRawRun(
    relevantRun?: EffluentPath,
    path?: Position[],
  ): AreaData | undefined {
    let effluentSpreader: EffluentTracker = this;

    if (relevantRun) {
      switch (relevantRun.pathType) {
        case EFFLUENT_PATH_TYPE.LateralEffluentSpreader:
          effluentSpreader = new LateralEffluentSpreader(this);
          break;
        case EFFLUENT_PATH_TYPE.PivotEffluentSpreader:
          effluentSpreader = new PivotEffluentSpreader(this);
          break;
      }

      return effluentSpreader.getSpreadOfRawRun(relevantRun, path);
    }
    return super.getSpreadOfRawRun(relevantRun);
  }

  /**
   * Function to get the spread LatLngs, and area (m^2), of the filtered run
   * @returns LatLngs and area (m^2) obj
   */
  getSpreadOfFilteredRun(relevantRun?: EffluentPath, path?: Position[]) {
    let effluentSpreader: EffluentTracker = this;

    if (relevantRun) {
      switch (relevantRun.pathType) {
        case EFFLUENT_PATH_TYPE.LateralEffluentSpreader:
          effluentSpreader = new LateralEffluentSpreader(this);
          break;

        case EFFLUENT_PATH_TYPE.PivotEffluentSpreader:
          effluentSpreader = new PivotEffluentSpreader(this);
          break;
      }
      return effluentSpreader.getSpreadOfFilteredRun(relevantRun, path);
    }

    return super.getSpreadOfFilteredRun();
  }

  getSpreadOfMappedRun(
    relevantRun: EffluentPath,
    path: Position[] = this.path,
  ) {
    let effluentSpreader: EffluentTracker = this;

    if (relevantRun) {
      switch (relevantRun.pathType) {
        case EFFLUENT_PATH_TYPE.LateralEffluentSpreader:
          effluentSpreader = new LateralEffluentSpreader(this);
          break;

        case EFFLUENT_PATH_TYPE.PivotEffluentSpreader:
          effluentSpreader = new PivotEffluentSpreader(this);
          break;
      }
      return effluentSpreader.getSpreadOfMappedRun(relevantRun, path);
    }

    return super.getSpreadOfMappedRun(relevantRun, path);
  }

  getUserAttributes(): UserAttributeDefs {
    return {
      ...super.getUserAttributes(),
      name: {
        type: "String",
        editable: true,
        label: "Name",
        name: "name",
        description: "Name of dam",
        required: true,
      },
      purpose: {
        type: "String",
        editable: true,
        label: "Purpose",
        name: "purpose",
        description: "Used for",
      },
      throwDistance: {
        type: "Number",
        editable: true,
        label: "Throw Radius (m)",
        name: "throwDistance",
        description: "Throw distance of effluent spreader",
        required: true,
      },
      flow: {
        type: "Number",
        editable: true,
        label: "Effluent Flow (L/min)",
        name: "flow",
        description: "The rate of liquid L/m",
        required: true,
      },
      location: {
        type: "RelatedType",
        editable: false,
        label: "Location",
        name: "Location",
        description: "Last position of the effluent spreader",
        objectTypeId: "Location" as ValidModelType,
        direction: PathDirection.child,
        edgeTypeId: "EffluentTracker_has_Location",
      },

      comments: {
        type: "String",
        editable: true,
        label: "Comments",
        name: "comments",
        description: "Free text for info ",
      },
    };
  }
}
